import React from "react";
import { Edit } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import { COLORS } from "../../constants/colors";

const offerStyles = {
  borderRadius: "8px",
  cursor: "pointer",
  position: "relative"
};

const activeOfferStyles = {
  ...offerStyles,
  border: `1px solid ${COLORS.WARM_GRAY}`
};

const inactiveOfferStyles = {
  ...offerStyles,
  border: `2px dashed ${COLORS.ERROR}`,
  backgroundColor: "#fff5f5"
};

// Helper function to determine offer visibility status and reason
const getOfferVisibilityStatus = (offer) => {
  const now = new Date();
  const reasons = [];

  if (!offer.isVisible) {
    reasons.push("isVisible is set to false");
  }

  if (offer.offerStartDate) {
    const startDate = new Date(offer.offerStartDate);
    if (startDate > now) {
      reasons.push("offer start date is in the future");
    }
  }

  if (offer.offerEndDate) {
    const endDate = new Date(offer.offerEndDate);
    if (endDate < now) {
      reasons.push("offer end date has passed");
    }
  }

  if (offer.rewardRedemption === true) {
    const availableCoupons =
      offer?.VendorOfferCoupons?.filter((c) => !c.expired).length ?? 0;
    if (availableCoupons === 0) {
      reasons.push("no unexpired coupon codes remaining");
    }
  }

  return {
    isVisible: reasons.length === 0,
    reasons
  };
};

export const OfferSection = ({ offers, title, onSelectOffer }) => {
  if (!offers?.length) {
    return null;
  }

  return (
    <>
      <Typography variant="h5" fontWeight="bold" mb={1}>
        {`${title} (${offers.length})`}
      </Typography>
      {offers.map((offer) => {
        const status = getOfferVisibilityStatus(offer);
        const sectionStyles = status.isVisible ? activeOfferStyles : inactiveOfferStyles;
        const hiddenReasons = offer.hiddenReasons ?? status.reasons;
        return (
          <Box
            key={offer.id}
            padding={2}
            mb={2}
            sx={sectionStyles}
            onClick={() => onSelectOffer?.(offer.id)}
            role="button"
          >
            <Edit sx={{ position: "absolute", top: 10, right: 10 }} />
            <Typography variant="subtitle2">
              <strong>ID:</strong> {offer.id}
            </Typography>
            <Typography variant="subtitle2">
              <strong>Title:</strong> {offer.offerTitle}
            </Typography>
            <Typography variant="subtitle2"><strong>Description:</strong> {offer.offerDescription}</Typography>

            <Typography variant="subtitle2">
              <strong>Start Date:</strong>{" "}
              {new Date(offer.offerStartDate).toLocaleDateString()}
            </Typography>
            <Typography variant="subtitle2">
              <strong>End Date:</strong>{" "}
              {offer.offerEndDate ? new Date(offer.offerEndDate).toLocaleDateString() : "--"}
            </Typography>
            {offer.dollarAmount > 0 && (
              <Typography variant="subtitle2">
                <strong>Dollar amount:</strong> ${offer.dollarAmount}
              </Typography>
            )}
            {offer.percentAmount > 0 && (
              <Typography variant="subtitle2">
                <strong>Percent amount:</strong> {offer.percentAmount}%
              </Typography>
            )}
            {Number(offer.metadata?.voucherMinimumPurchase ?? 0) > 0 && (
              <Typography variant="subtitle2">
                <strong>Minimum purchase amount:</strong> ${Number(offer.metadata.voucherMinimumPurchase).toFixed(2)}
              </Typography>
            )}

            {offer.rewardRedemption
              ? (
                <>
                  {offer.pointAmount != null && (
                    <Typography variant="subtitle2">
                      <strong>Points to redeem:</strong> {offer.pointAmount}
                    </Typography>
                  )}
                  <Typography variant="subtitle2">
                    <strong>Coupon codes remaining:</strong>{" "}
                    {offer?.VendorOfferCoupons?.filter((c) => !c.expired).length ?? 0}
                  </Typography>
                </>
              )
              : (
                offer.couponCode && (
                  <Typography variant="subtitle2"><strong>Coupon code: </strong>{offer.couponCode}</Typography>
                )
              )}

            {!status.isVisible && (
              <Box mt={1}>
                <Typography variant="subtitle1" color="error" fontWeight="bold">
                  <strong>Not visible/active in app due to reason(s):</strong>
                </Typography>
                {hiddenReasons.map((reason, idx) => (
                  <Typography key={idx} variant="subtitle2" color="error">
                    • {reason}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        );
      })}
    </>
  );
};
